import { Component, OnInit, OnDestroy, signal, HostListener, Inject, DOCUMENT, AfterViewInit } from '@angular/core';
import { environment as appEnvironment } from 'src/environments/environment';
import { purgeLegacyRaceCache } from 'src/functions/purge-legacy-race-cache.function';
import { savePartnerSessionHandoff } from 'src/functions/partner-session-handoff.function';
import { RACE_LOAD_ERROR_KEYS, RaceLoadErrorKind } from 'src/functions/race-load-error.function';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { ThemeService } from 'src/services/theme/theme.service';
import { Header } from './components/layouts/header/header';
import { Footer } from './components/layouts/footer/footer';
import { NgPlayground } from "./components/ng-playground/ng-playground";
import { SkeletonServiceService } from 'src/services/skeleton/skeleton.service';
import { StorageService } from 'src/services/storage/storage.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PmuService } from 'src/services/data/pmu.service';
import { UserManagerService } from 'src/services/userManager/user-manager.service';
import { environment } from 'src/environments/environment.dev';
import { GetProviderReqParams } from 'src/models/provider.model';
import { LocaleService } from 'src/services/locale/locale.service';
import { combineLatest, distinctUntilChanged, filter, map, of, Subscription, switchMap, tap } from 'rxjs';
import { createIdleListener, IdleListener } from 'src/functions/idle-listener.function';
import { handleViewportResize } from 'src/functions/viewport.function';
import { ConfigService } from 'src/services/config/config.service';
import { PartnerOperation } from 'src/models/partner-operation';
import { WebsocketService } from 'src/services/websocket/websocket.service';
import { Country } from 'src/models/country.model';
import { MyAccountService } from 'src/services/my-account/my-account.service';
import { TurfServiceService } from 'src/services/turf-service/turf-service.service';
import { LoggerService } from 'src/services/logger/logger.service';
import { SpinnerLoaderComponent } from './components/dialogues/spinner-loader/spinner-loader.component';
import { BetSlipState } from 'src/models/saloon-models/pari.model';
import { STORAGE_KEYS } from 'src/enums/storage-keys.enum';
import 'moment/locale/fr';
import 'moment/locale/en-gb';
import 'moment/locale/ar';
import { OperatorThemeService } from '@service/operator-theme/operator-theme.service';
import { OperatorConfigService } from '@service/operator-config/operator-config.service';
import { OperatorTranslationService } from '@service/operator-translation/operator-translation.service';
import { LanguageService } from 'src/services/language/language.service';
import { resolveLanguage } from 'src/functions/language-resolver.function';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, Header, Footer, SpinnerLoaderComponent, TranslateModule],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit, AfterViewInit, OnDestroy {
  subscription: Subscription = new Subscription();
  private idleListener: IdleListener | null = null;
  url = window.location.href;
  params: any = {};
  page: any;
  id!: any;
  session!: string;
  accountConfirmationObj = { status: '', msg: '', title: '' };
  validateCodeOptions: string[]=[];
  private queryLang: string | null = null;
  private queryLangApplied = false;
  private currentOperator: string | null = null;
  hideHeader: boolean = false;
  windowScrolled: boolean = false;
  loading: boolean = true;
  private mobileRedirectChecked: boolean = false;
  /** True while a hard hand-off to the /mobile app is still possible. */
  private mobileHandoffPending: boolean = false;
  /** subscribeToPartner() was called while the hand-off was pending. */
  private partnerHandshakeDeferred: boolean = false;

  constructor(
    private themeService: ThemeService,
    private uiService: SkeletonServiceService,
    private storage: StorageService,
    private translate: TranslateService,
    private pmuService: PmuService,
    private localeService: LocaleService,
    private operatorThemeService: OperatorThemeService,
    private operatorConfigService: OperatorConfigService,
    private operatorTranslationService: OperatorTranslationService,
    private languageService: LanguageService,
    private router: Router,
    private route: ActivatedRoute,
    private configsService: ConfigService,
    private userManagerService: UserManagerService,
    private accountService: MyAccountService,
    private socketHandler: WebsocketService,
    private turfService: TurfServiceService,
    private logger: LoggerService,
    private swUpdate: SwUpdate,
    @Inject(DOCUMENT) private document: Document
  ) {
      /** might be used */
      // const [entry] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];

      // if (entry?.type === 'reload') {
      //   sessionStorage.removeItem(STORAGE_KEYS.BETSLIP_STATE);
      // }

      // This app never uses hash routing (no HashLocationStrategy) — a stray
      // '#/...' fragment can only be leftover from a failed hard-navigation
      // elsewhere (e.g. redirectMobile() assigning /mobile/#/home?lang=..
      // and the browser bouncing back into this app because /mobile doesn't
      // really exist there, landing on the invalid /home#/home?lang=..).
      // Any query params meant for the destination app were embedded inside
      // that hash fragment (its own hash-router reads them from there), so
      // recover them into the real search string before Angular's Router
      // resolves the initial route — otherwise they'd just be dropped.
      if (/^#\//.test(window.location.hash)) {
        const hashQueryIndex = window.location.hash.indexOf('?');
        const recoveredSearch = hashQueryIndex >= 0
          ? window.location.hash.slice(hashQueryIndex)
          : window.location.search;
        history.replaceState(null, '', window.location.pathname + recoveredSearch);
      }

    this.subscription.add(this.route.queryParams.subscribe((params) => {
      let lang = params['lang'];
      let token = params['token'];
      let returnUrl = params['returnUrl'] || '';
      let operatorName = params['operator'];
      let events = params['events'] === '1';

      if (lang) {
        this.queryLang = lang.split('-')[0];
      }

      const current = configsService.partnerOperation;
      let newPartnerOp = current;

      if(lang && token && operatorName){
        this.userManagerService.logingIn = true;
        /**
         * new config to store returned info from partner about the user
         * to proceed in getting user info, balance and saving a bet
         */
        newPartnerOp = {
          lang,
          enabled: true,
          token,
          returnUrl,
          operatorName,
          events
        };
      } else if (operatorName && !current.enabled && operatorName !== current.operatorName) {
        newPartnerOp = {
          ...current,
          enabled: false,
          operatorName,
          lang: lang || current.lang,
          returnUrl: returnUrl || current.returnUrl,
        }
      }
      configsService.partnerOperation = newPartnerOp;

      // Only force the URL's lang once (on whichever emission first has it) -
      // this subscription lives for the whole session and re-fires on every
      // in-app navigation that preserves query params (queryParamsHandling:
      // 'merge'/'preserve', used throughout when partnerOperation.enabled).
      // Re-applying on every fire was stomping a later manual language
      // switch from the nav-menu/header pickers back to the link's original
      // lang, producing a toggling-back-and-forth between the two.
      if (this.isVirtual && this.queryLang && !this.queryLangApplied) {
        this.queryLangApplied = true;
        this.applyResolvedLanguage();
      }
    }));
    themeService.init();
    // Latched synchronously, before ngOnInit/ngAfterViewInit can run: it gates
    // the partner handshake so this app doesn't burn the single-use partner
    // token on a load that is about to be handed off to /mobile.
    this.mobileHandoffPending = this.isMobileHandoffCandidate();
    // TODO remove after 2 releases (target: end of 2026-11): drops the removed client-side race-card cache.
    purgeLegacyRaceCache(themeService.getTheme()?.id, appEnvironment.env);
    uiService.init();
    // Initialize viewport on component creation
    this.initializeViewport();
    this.initializeIdleListener();
   }
   get isVirtual(){
    return this.configsService.partnerOperation.enabled || this.themeService.getTheme().isVirtual;
  }
  /** Why the displayed day's race card failed to load (null while loading or loaded). */
  get gamesLoadError(): RaceLoadErrorKind | null {
    return this.pmuService.activeGamesError$.value;
  }

  /** i18n key of the race-load banner message for the current failure. */
  get gamesLoadErrorKey(): string {
    const kind = this.gamesLoadError;
    return kind ? RACE_LOAD_ERROR_KEYS[kind] : '';
  }

  get racesLoading(): boolean {
    return this.pmuService.loadingRaces$.value;
  }

  /** Race-load banner Refresh: reloads the displayed day, keeping the user's selections. */
  refreshGames(): void {
    this.pmuService.refreshActiveGames();
  }

   get theme() {
    return this.themeService.getTheme();
  }
  get showAngularPlayground() {
    return ['demo-theme', 'light-theme', 'dark-theme'].includes(this.themeService.getTheme().id)
  }

  get isMobile() {
    return this.uiService.isMobile();
  }
  // The hand-off is a LOAD-TIME decision and must not be re-evaluated here.
  // Candidacy depends on the viewport, so calling redirectMobile() on resize
  // would hard-navigate a tablet out of a live session (losing the betslip)
  // the moment it was rotated from landscape into portrait.
  @HostListener('window:resize', ['$event'])
  onWindowResize(event: Event): void {
    handleViewportResize(this.document);
  }

  /**
   * Whether this load is a candidate for the hard hand-off to the legacy
   * /mobile app. Synchronous (theme flag + user-agent + viewport + path).
   *
   * The user-agent and viewport tests are combined with AND, never OR: the
   * user-agent alone hands off a landscape tablet, and the viewport alone
   * hands off a resized/narrow *desktop* window — that OR is the bug fixed on
   * 2026-09-30 and must not come back. The path check prevents a redirection
   * loop when already on the mobile app.
   *
   * Mirrored by the pre-bootstrap script in src/index.mobile.html, which makes
   * the same decision before any bundle loads on the themes that ship it —
   * keep the two in sync.
   *
   * Deliberately stateless: nothing is stored about a previous attempt, so the
   * same tab, browser and conditions always produce the same outcome however
   * many times the user goes round the cycle. The /mobile path test is the
   * only thing that stops a redirection loop, and it needs no persistence.
   */
  private isMobileHandoffCandidate(): boolean {
    return this.themeService.getTheme().redirectMobile
      && this.configsService.isMobileDevice()
      && this.uiService.isMobile()
      && !window.location.pathname.startsWith('/mobile');
  }

  /**
   * /mobile turned out not to be reachable — this app keeps the partner
   * session after all, so run the handshake that A2 held back.
   */
  private releaseMobileHandoff(): void {
    if (!this.mobileHandoffPending) {
      return;
    }
    this.mobileHandoffPending = false;
    if (this.partnerHandshakeDeferred) {
      this.partnerHandshakeDeferred = false;
      this.subscribeToPartner();
    }
  }

  /**
   * Leave this app entirely. Its own method so a spec can assert the hand-off
   * without the test runner actually navigating away.
   */
  protected hardNavigate(url: string): void {
    window.location.assign(url);
  }

  private redirectMobile(){
    if (!this.isMobileHandoffCandidate()) {
      return;
    }

    // Belt-and-braces: redirectMobile() is only reached once per load now that
    // the resize listener no longer calls it, but the probe must never fire
    // twice for one load.
    if (this.mobileRedirectChecked) {
      return;
    }
    this.mobileRedirectChecked = true;

    // Taken and re-emitted VERBATIM, so every param survives the hand-off with
    // its percent-encoding intact - lang/token/operator/returnUrl and themeUrl,
    // plus anything added later. Never rebuild it field by field: themeUrl's own
    // query string contains encoded '&' and '=' that would not survive a
    // re-encode, and an unknown param would be silently dropped. Mirrored in
    // src/index.mobile.html, which makes the same hand-off before bootstrap.
    const queryString = window.location.search;

    // The mobile app is a separate deployment (not built/served by this repo)
    // expected at /mobile/ — it may not exist on every environment. Probe for
    // it first so a missing deployment falls back to this app's own desktop
    // home instead of a dead link / redirect loop.
    // Timed out so a hung probe can't strand a partner load on the spinner:
    // `logingIn` is set when the partner params are read and is only cleared
    // once the handshake completes, which releaseMobileHandoff() gates.
    fetch('/mobile/index.html', { method: 'HEAD', signal: AbortSignal.timeout(3000) })
      .then(response => {
        if (response.ok) {
          this.hardNavigate(`/mobile/#/home${queryString}`);
        } else {
          this.releaseMobileHandoff();
          this.router.navigateByUrl(`/home${queryString}`);
        }
      })
      .catch(() => {
        this.releaseMobileHandoff();
        this.router.navigateByUrl(`/home${queryString}`);
      });
  }

  /**
   * Initialize viewport handling
   */
  private initializeViewport(): void {
    handleViewportResize(this.document);
  }

  /**
   * Apply an operator's currency override, when it has one.
   *
   * Gated by theme.isVirtual for consistency with ConfigService.loadAppConfigs(),
   * which applies config.json's own currencyData under the same condition.
   */
  /**
   * Applies the operator's `languages`/`defaultLanguage` once its config
   * resolves (or times out -> {} -> theme list untouched). Virtual mode only;
   * independent of the theme-file fetch so a slow theme can't delay it.
   */
  private applyOperatorLanguages(operator: string): void {
    this.currentOperator = operator;
    if (!this.isVirtual) {
      return;
    }
    this.subscription.add(
      this.operatorConfigService.getOperatorConfig(operator).subscribe(config => {
        if (this.currentOperator !== operator) {
          return; // operator changed while the config was loading
        }
        this.languageService.applyOperator(config);
        // Re-resolve against the (possibly narrowed) offered list. Never
        // persists: the operator's list must not overwrite the saved choice.
        this.applyResolvedLanguage({ persist: false });
      })
    );
  }

  private applyOperatorCurrency(operator: string): void {
    if (!this.themeService.getTheme()?.isVirtual) {
      return;
    }

    this.operatorConfigService.getOperatorCurrency(operator).subscribe(currency => {
      if (currency) {
        this.configsService.currencyData = currency;
        this.configsService.currency = currency.strCode;
      }
    });
  }

  ngOnInit(): void {
    this.subscription.add(
      this.route.queryParamMap
        .pipe(
          // Keyed on the pair, not on the operator alone: the design-token file
          // is now named by themeUrl, so the same operator pointing at a
          // different file has to re-theme. The operator is lowercased (it's an
          // id we match against our registry); the URL never is - path and query
          // are case-sensitive.
          map(params => ({
            operator: (params.get('operator') || '').trim().toLowerCase() || null,
            themeUrl: params.get('themeUrl') || null
          })),

          distinctUntilChanged((a, b) => a.operator === b.operator && a.themeUrl === b.themeUrl),

          switchMap(({ operator, themeUrl }) => {

            if (!operator) {
              this.operatorThemeService.clearOperatorTheme();
              this.operatorTranslationService.clearOperatorTranslations();
              this.currentOperator = null;
              if (this.languageService.clearOperator()) {
                this.applyResolvedLanguage({ persist: false });
              }

              return of(false);
            }

            this.applyOperatorLanguages(operator);

            // Currency and translations still follow the operator (those files
            // stay ours, in the bucket); only the theme comes from themeUrl, so
            // without one there is nothing to apply - drop any colors a previous
            // operator/URL had set.
            if (!themeUrl) {
              this.operatorThemeService.clearOperatorTheme();
              this.applyOperatorCurrency(operator);
              this.operatorTranslationService.applyOperatorTranslations(operator).subscribe();

              return of(false);
            }

            return this.operatorThemeService
              .applyOperatorTheme(operator, themeUrl)
              .pipe(
                tap(() => this.applyOperatorCurrency(operator)),
                tap(() => this.operatorTranslationService.applyOperatorTranslations(operator).subscribe())
              );
          })
        )
        .subscribe()
    );

    this.subscription.add(combineLatest([
      this.uiService.configLoading$,
      this.router.events])
      .pipe(map(([a, c]) => {
        return [(a || (c instanceof NavigationStart)), (c instanceof NavigationEnd)];
      })).subscribe(([loading, navEnd]) => {
        if(navEnd){
          this.loading = false;
        }else {
          this.loading = loading;
        }
      }));

    this.applyResolvedLanguage();
    this.loadData();
    this.subscription.add(this.userManagerService.onLogIn.subscribe((res) => {
      if(res){
        this.loadUserDataExtras();
      }
    }));

    this.redirectMobile();

    this.subscription.add(
      combineLatest([this.pmuService.getACData(), this.pmuService.paymentProviders$]).pipe(distinctUntilChanged()).subscribe(([{countries, operators}, providersValue]) => {
        if (countries?.length) {
            this.pmuService.updateBetOptions({countries: countries});
          }
          if (operators?.length) {
            this.pmuService.updateBetOptions({operators: operators});
          }
          if(providersValue["CASHIN"]?.length){
            this.pmuService.updateBetOptions({providers: providersValue['CASHIN']})
          }
          // this.betPayOptions.set(this.pmuService.getBetOptionsData())
        })
    );
    this.userManagerService.getCountryList().then(resp => {
      this.userManagerService.countries.forEach((country: Country) => {
        this.userManagerService.dialingCodes.push(country.dialling_code);
      });
      this.userManagerService.dialingCodes.sort();
    });
    this.subscription.add(this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((val: NavigationEnd) => {
        this.scrollToTop();
        if (val.url && val.url.indexOf("age-verification") > -1) {
          this.hideHeader = true;
        } else {
          this.hideHeader = false;
        }
    }));
    this.subscription.add(this.turfService.courseChange$.subscribe((course) => {
      this.logger.log("Course change detected in app component", course);
      if (course?.length) {
        this.pmuService.updateCourse(course);
      }

    }));

    this.watchForAppUpdates();
  }

  /**
   * Every new build produces a distinct content hash for ngsw.json, so once
   * the service worker (registered in app.config.ts) detects a new version
   * is available, reload immediately rather than silently keeping users on
   * stale cached assets until their next full navigation/restart.
   * `unrecoverable` (the SW's own internal state is corrupt, e.g. a chunk it
   * expected is now 404 after a deploy) is handled the same way, since a
   * reload is also the correct recovery there.
   */
  private watchForAppUpdates(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.subscription.add(
      this.swUpdate.versionUpdates
        .pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
        .subscribe(() => {
          this.document.location.reload();
        })
    );

    this.subscription.add(
      this.swUpdate.unrecoverable.subscribe(() => {
        this.document.location.reload();
      })
    );
  }

  /**
   * Single language resolution used at every startup step (query-param
   * capture, ngOnInit, ngAfterViewInit, operator config arrival). Rules live
   * in resolveLanguage(); virtual + valid ?lang= is applied without touching
   * the saved 'pmu-lang-web' choice and locks the switcher.
   */
  private applyResolvedLanguage(options: { persist?: boolean } = {}): string {
    const resolution = resolveLanguage({
      isVirtual: this.isVirtual,
      queryLang: this.queryLang,
      saved: this.storage.get('pmu-lang-web'),
      browser: this.translate.getBrowserLang(),
      userLang: this.userManagerService.userData?.lang,
      offered: this.languageService.offeredCodes,
      defaultLang: this.languageService.defaultLanguage
    });
    this.languageService.setLocked(resolution.locked);
    this.uiService.changeAppLanguage(resolution.lang, {
      persist: resolution.persist && (options.persist ?? true)
    });
    return resolution.lang;
  }

  ngAfterViewInit(): void {
    this.socketHandler.connectRaces();
    // Re-resolve now that userData may be restored; never persists (this step
    // historically only set the active language, not the saved choice).
    this.configsService.lang = this.applyResolvedLanguage({ persist: false });
    if (this.themeService.useGateway) {
      if (this.url.split('?').length > 1 && !this.configsService.partnerOperation.enabled) {
        if (this.url.includes('?') && ['page', 'ValidateCode', 'session'].some((p) => this.url.includes(p))) {
          var dataContext = this.url.split('?')[1];
          this.params = {};
          dataContext =
            dataContext && dataContext.includes('#/&')
              ? dataContext.split('#/&')[1]
              : dataContext;
          if (dataContext) {
            const dataArray = dataContext.split('&');

            dataArray.forEach((data) => {
              let keyValue = data.split('=');
              this.params[keyValue[0]] = keyValue[1];
            });

            this.page = this.params['page'];

            if (this.params['ValidateCode']) {
              this.page = 'confirmation';
              this.validateCodeOptions = this.params['ValidateCode'].split('@').concat(this.params['lang']);
            }
            this.openPage();
          }
          this.id = { session: this.params['session'] };
          this.router.navigate([], {
            queryParams: {
              session: null,
            },
            queryParamsHandling: 'merge'
          });
        } else {
          this.id = undefined;
        }
      }

      if (this.storage.checkCookie('session')) {
        this.session = this.storage.getCookie('session');
      }
      if (this.session || this.id) {
        this.userManagerService.authSession(this.id ? this.id : { session: this.session }).then(res => {
          if (this.themeService.useGateway) {
            this.socketHandler.connectSession();
          }
          window.location.href.indexOf('registration') > -1 ? this.router.navigate(['/home'], {queryParamsHandling: 'preserve'}) : ""
        });
        this.subscribeToPartner();
        return;
      }


    } else {
      let userData = this.storage.getObject('user', {});
      if (userData.hasOwnProperty("account_code")) {
        this.userManagerService.userData = userData;
        this.userManagerService.onLogIn.next(1);
      }

    }

    this.subscribeToPartner();
  }

  @HostListener("window:scroll", [])
  onWindowScroll() {
    let rootElement = document.documentElement;
    let scrollTotal = rootElement.scrollHeight - rootElement.clientHeight;
    if ((rootElement.scrollTop / scrollTotal ) > 0.2) {
      this.windowScrolled = true;
    } else {
      this.windowScrolled = false;
    }
  }

  openPage() {
    let url = "";
    let state: any;
    switch (this.page) {
      case 'register':
        url = this.configsService.quick_signup_enabled == 1 ? 'quick-signup': 'signup';
        break;
      case "confirmation":
        url = "/user/confirmation";
        state = {validationOpts: this.validateCodeOptions, hideHeader: true};
        break;
      default: break;
    }
    this.router.navigate([url], {state, queryParamsHandling: 'merge'});

  }
  loadPartnerUserData(p: PartnerOperation, authToken: string){
    const fail = (e?: any) => this.userManagerService.exitPartnerSession(e?.errorCode);
    this.subscription.add(this.userManagerService.getUserSessionFromPartner(p.operatorName, p.token, p.lang, authToken).subscribe({
      next: (partner) => {
        if (!partner) return fail();
        partner.operatorName = p.operatorName;
        this.userManagerService.mapPartnerUser(partner, 0, p.lang);
        // The session is established: publish it for the /mobile app in case
        // this tab is handed off later. The partner token cannot be redeemed
        // twice, so that app must inherit this session rather than re-activate.
        savePartnerSessionHandoff(p, partner);
        this.subscription.add(this.userManagerService.loadUserBalanceFromPartner(partner).subscribe({
          next: (balance) => {
            if (balance === null) return fail();
            this.userManagerService.userBalance = balance;
            this.userManagerService.onLogIn.next(1);
            this.userManagerService.logingIn = false;
          },
          error: fail,
        }));
      },
      error: fail,
    }));
  }

  subscribeToPartner(){
    // A real mobile device is about to be handed off to the /mobile app, which
    // runs this exact same handshake. The partner token backs a single
    // activation (POST web/v1/iframe/activate) — running it here too burns the
    // token and double-activates the player on the operator side. pmu-mobile
    // owns the session; only run here once the hand-off is ruled out, i.e. from
    // releaseMobileHandoff().
    if (this.mobileHandoffPending) {
      this.partnerHandshakeDeferred = true;
      return;
    }

    // Only (operatorName, token) identify a distinct partner session - re-fire the
    // token/session/balance exchange when either actually changes, but not on a
    // language-only partnerOperation re-emission (merge keeps operator/token as-is,
    // so distinctUntilChanged sees no change and skips it).
    this.subscription.add(this.configsService.partnerOperation$.pipe(
      filter((p) => p.enabled),
      distinctUntilChanged((a, b) => a.operatorName === b.operatorName && a.token === b.token)
    ).subscribe((p) => {
        this.subscription.add(this.userManagerService.getPartnerUserAuthToken().subscribe((token) => {
          if(token){
              this.loadPartnerUserData(p, token);
          }else {
            this.userManagerService.exitPartnerSession();
          }
        }));
    }))
  }


  /**
   * Initialize idle listener to track user activity
   */
  private initializeIdleListener(): void {
    this.idleListener = createIdleListener(this.document, {
      onActivity: () => {
        // Update last active time in user manager service
        this.userManagerService.lastActiveTime = new Date();
      },
      onIdle: () => {
        // Handle idle timeout - called when user is inactive for 30 seconds
        console.log('User has been idle for 30 seconds');

        // Optional: Add custom idle timeout logic here
        // this.userManagerService.handleIdleTimeout();
      }
    });
  }

  /**
   * Clean up event listeners and timers
   */
  ngOnDestroy(): void {
    // Clean up idle listener
    if (this.idleListener) {
      this.idleListener.destroy();
    }

    // Clean up subscriptions
    this.subscription.unsubscribe();
  }

  loadData() {
    this.fetchConfigs();
    this.getActiveGames();
    if(!this.configsService.partnerOperation.enabled){
      this.getCashInProviders();
    }
  }

  getCashInProviders() {
   this.userManagerService.validProviders = [];
    this.accountService.getProviders(1).then(data => {
      if(this.accountService.paymentProviders){
        for (let provider of this.accountService.paymentProviders[1]) {
          if (provider.hasOwnProperty('mask') && provider.mask.trim() != "") {
            let mask = provider.mask.split('#')[0];
            if (mask && mask.split('-').length > 0) {
              for (let item of mask.split('-')) {
                this.userManagerService.validProviders.push(item)
              }
            }
            else if (mask && mask.split('-').length == 1) {
              this.userManagerService.validProviders.push(mask)
            }

          }
        }
      }
    });
 }

  loadUserDataExtras() {
    this.userManagerService.fetchUserTransactionExtras().then(res => {
      if('erc' in res && res.erc == 1) {
        this.updateData(res.data)
      }
    })
  }

  updateData(data: any){
    Object.keys(data).forEach(element => {
        this.userManagerService.userData[element] = data[element];
    });
  }

 fetchConfigs() {
   this.uiService.getSystemConfigs();
 }

  getActiveGames() {
    let savedData: BetSlipState = this.storage.getSessionItem(STORAGE_KEYS.BETSLIP_STATE);
    let value: Date;
    let restore = false;

    if (savedData.selectedDate && new Date(savedData.selectedDate) >= new Date()) {
      value = new Date(savedData.selectedDate);
      restore = savedData.pari ? Object.keys(savedData.pari).length > 0 : false;
    } else {
      this.storage.deleteSessionItem(STORAGE_KEYS.BETSLIP_STATE);
      value = new Date();
    }

    this.pmuService.getActiveGames(value, restore);
  }

 scrollToTop() {
    let rootElement = document.documentElement;

    rootElement.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  }
}
